import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAutoBuyParameters } from './hooks/useAutoBuyParameters';
import { useAutoBuyApi } from './hooks/useAutoBuyApi';
import ParameterRow from './components/ParameterRow';
import AutobuyActions from './components/AutobuyActions';
import RuleCard from './components/RuleCard';
import { AutobuyContentProps } from './types';

export default function AutobuyContent({ swarmId, isAutoBuyActive }: AutobuyContentProps) {
  // Custom hooks to manage state and API interactions
  const {
    autoBuyState,
    updateParametersFromData,
    handleParameterChange,
    handleTicketsGoalChange,
    handleLimitedViewChange,
    handlePriceRangeChange,
    extractRangeValues,
    extractAlphanumericRangeValues,
    handleAddParameter,
    handleDeleteParameter,
    prepareDataForSaving,
    validationErrors,
    hasErrors,
    validateAllParameters
  } = useAutoBuyParameters();

  const {
    rulesData,
    isLoading,
    isSaving,
    isTogglingAutoBuy,
    error,
    handleSaveRules,
    handleToggleAutoBuys
  } = useAutoBuyApi(swarmId);

  // Effect to load initial rules
  useEffect(() => {
    if (rulesData) {
      updateParametersFromData(rulesData);
    }
  }, [rulesData, updateParametersFromData]);

  // Function to save changes
  const handleSave = async () => {
    // Validate all parameters before saving
    const isValid = validateAllParameters();
    
    if (isValid) {
      // If validation passes, proceed with saving
      const dataToSave = prepareDataForSaving();
      await handleSaveRules(dataToSave);
    } else {
      // If validation fails, we don't need to do anything else
      // The errors will be displayed in the UI due to updated validationErrors state
      console.log('Validation failed, not saving');
    }
  };

  // Function to toggle auto buys
  const handleToggleAutoBuy = () => {
    // Validate all parameters before toggling auto buys
    const isValid = validateAllParameters();
    
    if (isValid) {
      // If validation passes, proceed with saving and toggling
      const dataToSave = prepareDataForSaving();
      handleToggleAutoBuys(isAutoBuyActive, dataToSave);
    } else {
      // If validation fails, we don't need to do anything else
      // The errors will be displayed in the UI due to updated validationErrors state
      console.log('Validation failed, not toggling auto buys');
    }
  };

  // State for tracking which parameter is being deleted
  const [deletingParam, setDeletingParam] = useState<string | null>(null);

  const handleDelete = async (tempId: string) => {
    // 1) Search for the complete parameter
    const param = autoBuyState.parameters.find(p => p.tempId === tempId);
    if (!param) return;
  
    // 2) If it doesn't have an id, it was a new parameter without persistence: 
    //    we only delete it locally and exit
    if (!param.id) {
      handleDeleteParameter(tempId);
      return;
    }
  
    // 3) If we reach this point, it has an id: we make the remote deletion
    try {
      setDeletingParam(tempId);
  
      // We prepare the request to the backend
      const dataToSave = {...param, isDeleted: true}
      console.log('Data to save (delete):', dataToSave);
  
      // We call the API with the 'delete' flag
      const result = await handleSaveRules([dataToSave], 'delete');
      if (result.success) {
        console.log('Rule deleted successfully');
      }
    } catch (err) {
      console.error('Error deleting rule:', err);
    } finally {
      setDeletingParam(null);
    }
  };

  return (
    <Box sx={{ p: 2, maxHeight: 'calc(85vh - 100px)', overflow: 'auto' }}>
      {/* Loading state - initial data load */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading AutoBuy rules: {JSON.stringify(error)}
        </Alert>
      )}
      
      {/* Parameters section - only show when not loading */}
      {!isLoading && autoBuyState.parameters.map(param => (
        <RuleCard 
          key={param.tempId} 
          accepted={param.accepted} 
          completed={param.completed} 
          complete={param.ticketsGoalReached}
          ticketsGoal={param.ticketsGoal}
        >
          <ParameterRow
            param={param}
            errors={validationErrors[param.tempId] || {}}
            onParameterChange={handleParameterChange}
            onTicketsGoalChange={(tempId, e) => handleTicketsGoalChange(tempId, e.target.value)}
            onLimitedViewChange={(tempId, e) => handleLimitedViewChange(tempId, e.target.checked)}
            onPriceRangeChange={handlePriceRangeChange}
            onDeleteParameter={handleDelete}
            extractRangeValues={extractRangeValues}
            extractAlphanumericRangeValues={extractAlphanumericRangeValues}
            isDeletingParam={deletingParam}
          />
        </RuleCard>
      ))}

      {/* Add new parameters button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddParameter}
          disabled={isLoading}
        >
          Add New Parameters
        </Button>
      </Box>

      {/* Actions row */}
      <AutobuyActions
        onToggleAutoBuys={handleToggleAutoBuy}
        onSave={handleSave}
        isLoading={isLoading || isTogglingAutoBuy}
        isSaving={isSaving}
        hasErrors={hasErrors()}
        isAutoBuyActive={isAutoBuyActive}
      />
    </Box>
  );
}
